import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, ImageIcon } from "lucide-react";
import { siteShot } from "@/data/site";
import { easeLux } from "@/lib/motion";

// Modal de detalle: sitios web muestran vista previa grande + botón para visitar;
// proyectos de imagen muestran una vista ampliada (marcador por ahora).
export const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="project-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: easeLux }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-plum/70 backdrop-blur-md"
            onClick={onClose}
            data-testid="project-modal-backdrop"
          />

          {/* Contenido */}
          <motion.div
            className="relative z-10 flex w-full max-w-5xl max-h-[88vh] flex-col overflow-hidden bg-ivory"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.55, ease: easeLux }}
            data-lenis-prevent
          >
            <button
              onClick={onClose}
              aria-label="Cerrar"
              data-testid="project-modal-close"
              className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-ivory/90 text-plum backdrop-blur transition-[background-color,color] duration-500 hover:bg-plum hover:text-ivory"
            >
              <X strokeWidth={1.5} />
            </button>

            {/* Media */}
            <div className="relative w-full overflow-y-auto" data-lenis-prevent>
              {project.type === "web" ? (
                <div className="bg-powder/30">
                  <img
                    src={siteShot(project.url, 1400, 1000)}
                    alt={project.title}
                    data-testid="project-modal-image"
                    className="h-auto w-full object-cover"
                    loading="eager"
                  />
                </div>
              ) : (
                <div className="flex h-72 w-full flex-col items-center justify-center bg-powder/50 md:h-96">
                  <ImageIcon className="h-12 w-12 text-plum/40" strokeWidth={1} />
                  <span className="mt-4 text-[0.65rem] uppercase tracking-[0.3em] text-plum/60">
                    Próximamente
                  </span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-col gap-4 border-t border-rosegold/25 p-7 md:flex-row md:items-end md:justify-between md:p-9">
              <div>
                <p className="text-[0.6rem] uppercase tracking-[0.3em] text-rosegold">
                  {project.category} · {project.year}
                </p>
                <h3 className="mt-2 font-serif text-3xl md:text-4xl text-plum">{project.title}</h3>
                <p className="mt-3 max-w-lg font-sans font-light text-sm leading-relaxed text-charcoal/70">
                  {project.description}
                </p>
              </div>

              {project.type === "web" && (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-modal-visit"
                  className="group inline-flex shrink-0 items-center justify-center gap-3 rounded-full bg-rosegold px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-ivory transition-[background-color,transform] duration-500 hover:-translate-y-0.5 hover:bg-plum"
                >
                  Visitar sitio web
                  <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                </a>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
